import * as React from "react";
import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {borderRadius, display, flexDirection} from "@pg-design/helpers-css";
import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";
import {useIsMobile, useIsMounted} from "@pg-mono/hooks";
import {appendQueryString} from "@pg-mono/request";
import {decimalSeparatorThousand, pluralize} from "@pg-mono/string-utils";

import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {getHomeLinkClick} from "../../../tracking/algolytics_hits/click_actions/gethome_link_click";
import {ViewType} from "../../../view_type/ViewType";
import {IRegionAncestor} from "../../list/actions/offer_list_selected_data_actions";

export const getHomeLogo = require("../../images/gethome_logo.png") as string;
export const permalinkBase = "https://gethome.pl/permalinks/rp/offer/list/";

interface IOfferDetailGetHomeLink {
    region: string;
    regionId: number;
    offer: {
        id: number;
        name: string;
        region: {
            id: number;
        };
        vendor: {
            name: string;
        };
    };
    stats: {
        region_type_district: IRegionAncestor | null;
    } | null;
    filterData?: ISearchFiltersFormValues | null;
    className?: string;
}

export const OfferDetailGetHomeLink = (props: IOfferDetailGetHomeLink) => {
    const {filterData, className} = props;
    const isMounted = useIsMounted();
    const isMobile = useIsMobile();

    const getRoomLabel = () => {
        const roomsWho = pluralize(["pokój", "pokoje", "pokoi"]);
        const roomsWhom = pluralize(["pokoju", "pokoi", "pokoi"]);
        const lower = filterData?.rooms.lower;
        const upper = filterData?.rooms.upper;

        // open bounds
        if (!lower && !upper) {
            return;
        }
        if (!lower) {
            // special case for half-opened minimal range
            return <span css={boldTextStyle}>{upper === 1 ? `${upper} pokojowego ` : `do ${upper} ${roomsWhom(upper as number)} `}</span>;
        }
        if (!upper) {
            return <span css={boldTextStyle}>{`od ${lower} ${roomsWhom(lower as number)} `}</span>;
        }
        // defined bounds
        if (lower === upper) {
            return <span css={boldTextStyle}>{`${lower} pokojowego `}</span>;
        }
        const optionalPlus = (v: number) => (v === 5 ? "5+" : v);
        return <span css={boldTextStyle}>{`${lower} - ${optionalPlus(upper as number)} ${roomsWho(upper as number)} `}</span>;
    };

    const getAreaVerbose = () => {
        const areaLower = filterData?.area.lower;
        const areaUpper = filterData?.area.upper;

        if (areaLower || areaUpper) {
            return (
                <>
                    o metrażu
                    <span css={boldTextStyle}>
                        {areaLower && ` od ${decimalSeparatorThousand(areaLower)}`}
                        {areaUpper && ` do ${decimalSeparatorThousand(areaUpper)}`}
                        <span className="nowrap">
                            m<sup>2</sup>&nbsp;
                        </span>
                    </span>
                </>
            );
        }
    };

    const getPriceVerbose = () => {
        const priceLower = filterData?.price.lower;
        const priceUpper = filterData?.price.upper;

        if (priceLower || priceUpper) {
            return (
                <>
                    w cenie
                    <span css={boldTextStyle}>
                        {priceLower && ` od ${decimalSeparatorThousand(priceLower)}`}
                        {priceUpper && ` do ${decimalSeparatorThousand(priceUpper)}`} zł&nbsp;
                    </span>
                </>
            );
        }
    };

    const redirectToGH = () => {
        // algolytics hit
        getHomeLinkClick(ViewType.OFFER);

        const query = {
            area_0: filterData?.area.lower || undefined,
            area_1: filterData?.area.upper || undefined,
            price_0: filterData?.price.lower || undefined,
            price_1: filterData?.price.upper || undefined,
            rooms_0: filterData?.rooms.lower || undefined,
            rooms_1: filterData?.rooms.upper || undefined,
            region: props.regionId,
            utm_source: "rynek_pierwotny",
            utm_medium: "offer",
            utm_campaign: "crosspromo_rynek_pierwotny"
        };
        const url = appendQueryString(permalinkBase, query);
        window.open(url, "_blank");
    };

    return isMounted ? (
        <div className={className} css={offerDetailGetHomeLink} onClick={redirectToGH}>
            <Picture css={logoImage} sources={[{minWidthPX: 0, src: getHomeLogo, width: 175, height: 40}]} alt="gethome" loading="lazy" />
            <div css={[display("flex"), flexDirection("column")]}>
                <Text as="p" css={questionTextStyle}>
                    Szukasz mieszkania&nbsp;
                    {getRoomLabel()}
                    {getAreaVerbose()}
                    {getPriceVerbose()}w lokalizacji <span css={boldTextStyle}>{props.stats?.region_type_district?.name || props.region}?</span>
                </Text>
                <Text as="p" variant="body_copy_1" css={teaserTextStyle}>
                    Zobacz najnowsze oferty z rynku wtórnego
                </Text>
                <Button variant="filled_primary" size={isMobile ? "x-small" : "small"}>
                    Zobacz oferty
                </Button>
            </div>
        </div>
    ) : null;
};

const offerDetailGetHomeLink = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.4rem;
    ${borderRadius(4)}
    background: #fff;
    cursor: pointer;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        padding: 2.4rem 8.9rem;
        width: calc(288px * 3 + 1.6rem * 2); // PropertyBox width times 3 + margin between boxes times 2
        margin: 0 auto;
    }
`;

const logoImage = (theme: Theme) => css`
    margin: 4.8rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 8.8rem 0 0;
    }
`;

const questionTextStyle = css`
    margin: 0 0 2.5rem 0;
`;

const teaserTextStyle = (theme: Theme) => css`
    color: ${theme.colors.gray["700"]};
    margin: 0 0 2.5rem 0;
`;

const boldTextStyle = css`
    font-weight: 500;
`;
import * as React from "react";
import {css, Theme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {borderRadius, display, flexDirection} from "@pg-design/helpers-css";
import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";
import {useIsMobile, useIsMounted} from "@pg-mono/hooks";
import {appendQueryString} from "@pg-mono/request";
import {decimalSeparatorThousand, pluralize} from "@pg-mono/string-utils";

import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {getHomeLinkClick} from "../../../tracking/algolytics_hits/click_actions/gethome_link_click";
import {ViewType} from "../../../view_type/ViewType";
import {IRegionAncestor} from "../../list/actions/offer_list_selected_data_actions";

export const getHomeLogo = require("../../images/gethome_logo.png") as string;
export const permalinkBase = "https://gethome.pl/permalinks/rp/offer/list/";

interface IOfferDetailGetHomeLink {
    region: string;
    regionId: number;
    offer: {
        id: number;
        name: string;
        region: {
            id: number;
        };
        vendor: {
            name: string;
        };
    };
    stats: {
        region_type_district: IRegionAncestor | null;
    } | null;
    filterData?: ISearchFiltersFormValues | null;
    className?: string;
}

export const OfferDetailGetHomeLink = (props: IOfferDetailGetHomeLink) => {
    const {filterData, className} = props;
    const isMounted = useIsMounted();
    const isMobile = useIsMobile();

    const getRoomLabel = () => {
        const roomsWho = pluralize(["pokój", "pokoje", "pokoi"]);
        const roomsWhom = pluralize(["pokoju", "pokoi", "pokoi"]);
        const lower = filterData?.rooms.lower;
        const upper = filterData?.rooms.upper;

        // open bounds
        if (!lower && !upper) {
            return;
        }
        if (!lower) {
            // special case for half-opened minimal range
            return <span css={boldTextStyle}>{upper === 1 ? `${upper} pokojowego ` : `do ${upper} ${roomsWhom(upper as number)} `}</span>;
        }
        if (!upper) {
            return <span css={boldTextStyle}>{`od ${lower} ${roomsWhom(lower as number)} `}</span>;
        }
        // defined bounds
        if (lower === upper) {
            return <span css={boldTextStyle}>{`${lower} pokojowego `}</span>;
        }
        const optionalPlus = (v: number) => (v === 5 ? "5+" : v);
        return <span css={boldTextStyle}>{`${lower} - ${optionalPlus(upper as number)} ${roomsWho(upper as number)} `}</span>;
    };

    const getAreaVerbose = () => {
        const areaLower = filterData?.area.lower;
        const areaUpper = filterData?.area.upper;

        if (areaLower || areaUpper) {
            return (
                <>
                    o metrażu
                    <span css={boldTextStyle}>
                        {areaLower && ` od ${decimalSeparatorThousand(areaLower)}`}
                        {areaUpper && ` do ${decimalSeparatorThousand(areaUpper)}`}
                        <span className="nowrap">
                            m<sup>2</sup>&nbsp;
                        </span>
                    </span>
                </>
            );
        }
    };

    const getPriceVerbose = () => {
        const priceLower = filterData?.price.lower;
        const priceUpper = filterData?.price.upper;

        if (priceLower || priceUpper) {
            return (
                <>
                    w cenie
                    <span css={boldTextStyle}>
                        {priceLower && ` od ${decimalSeparatorThousand(priceLower)}`}
                        {priceUpper && ` do ${decimalSeparatorThousand(priceUpper)}`} zł&nbsp;
                    </span>
                </>
            );
        }
    };

    const redirectToGH = () => {
        // algolytics hit
        getHomeLinkClick(ViewType.OFFER);

        const query = {
            area_0: filterData?.area.lower || undefined,
            area_1: filterData?.area.upper || undefined,
            price_0: filterData?.price.lower || undefined,
            price_1: filterData?.price.upper || undefined,
            rooms_0: filterData?.rooms.lower || undefined,
            rooms_1: filterData?.rooms.upper || undefined,
            region: props.regionId,
            utm_source: "rynek_pierwotny",
            utm_medium: "offer",
            utm_campaign: "crosspromo_rynek_pierwotny"
        };
        const url = appendQueryString(permalinkBase, query);
        window.open(url, "_blank");
    };

    return isMounted ? (
        <div className={className} css={offerDetailGetHomeLink} onClick={redirectToGH}>
            <Picture css={logoImage} sources={[{minWidthPX: 0, src: getHomeLogo, width: 175, height: 40}]} alt="gethome" loading="lazy" />
            <div css={[display("flex"), flexDirection("column")]}>
                <Text as="p" css={questionTextStyle}>
                    Szukasz mieszkania&nbsp;
                    {getRoomLabel()}
                    {getAreaVerbose()}
                    {getPriceVerbose()}w lokalizacji <span css={boldTextStyle}>{props.stats?.region_type_district?.name || props.region}?</span>
                </Text>
                <Text as="p" variant="body_copy_1" css={teaserTextStyle}>
                    Zobacz najnowsze oferty z rynku wtórnego
                </Text>
                <Button variant="filled_primary" size={isMobile ? "x-small" : "small"}>
                    Zobacz oferty
                </Button>
            </div>
        </div>
    ) : null;
};

const offerDetailGetHomeLink = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.4rem;
    ${borderRadius(4)}
    background: #fff;
    cursor: pointer;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        padding: 2.4rem 8.9rem;
        width: calc(288px * 3 + 1.6rem * 2); // PropertyBox width times 3 + margin between boxes times 2
        margin: 0 auto;
    }
`;

const logoImage = (theme: Theme) => css`
    margin: 4.8rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 8.8rem 0 0;
    }
`;

const questionTextStyle = css`
    margin: 0 0 2.5rem 0;
`;

const teaserTextStyle = (theme: Theme) => css`
    color: ${theme.colors.gray["700"]};
    margin: 0 0 2.5rem 0;
`;

const boldTextStyle = css`
    font-weight: 500;
`;
